import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, Sparkles } from 'lucide-react';
import { services } from '@/data/index';
import { ROUTE_PATHS } from '@/lib/index';
import { IMAGES } from '@/assets/images';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { springPresets, fadeInUp, staggerContainer, staggerItem } from '@/lib/motion';

export default function Services() {
  return (
    <div className="min-h-screen">
      <section className="relative min-h-[60vh] flex items-center justify-center overflow-hidden">
        <div className="hero-background">
          <img
            src={IMAGES.CONSULTING_4}
            alt="Professional Services"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="hero-overlay" />
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 32 }}
            animate={{ opacity: 1, y: 0 }}
            transition={springPresets.gentle}
            className="max-w-4xl mx-auto text-center"
          >
            <Badge className="mb-6 text-base px-6 py-2">
              <Sparkles className="w-4 h-4 mr-2" />
              AI-Enhanced Financial Services
            </Badge>
            <h1 className="text-4xl md:text-6xl font-bold mb-6 tracking-tight leading-tight">
              Transform Your Financial Future
            </h1>
            <p className="text-xl md:text-2xl text-muted-foreground mb-8 max-w-3xl mx-auto">
              Comprehensive credit repair, tax services, and business consulting powered by cutting-edge AI technology and human expertise.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" asChild className="text-lg px-8">
                <Link to={ROUTE_PATHS.CONTACT}>
                  Get Started Today
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Link>
              </Button>
              <Button size="lg" variant="outline" asChild className="text-lg px-8">
                <a href="#services-detail">Explore Services</a>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      <section id="services-detail" className="py-24 bg-muted/30">
        <div className="container mx-auto px-4">
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            className="space-y-32"
          >
            {services.map((service, index) => (
              <motion.div
                key={service.id}
                variants={staggerItem}
                className={`grid md:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? 'md:flex-row-reverse' : ''}`}
              >
                <div className={`${index % 2 === 1 ? 'md:order-2' : ''}`}>
                  <Badge className="mb-4">{service.title}</Badge>
                  <h2 className="text-4xl md:text-5xl font-bold mb-6 tracking-tight">
                    {service.title}
                  </h2>
                  <p className="text-lg text-muted-foreground mb-8">
                    {service.description}
                  </p>

                  <div className="space-y-8">
                    <div>
                      <h3 className="text-2xl font-semibold mb-4 flex items-center gap-2">
                        <Check className="w-6 h-6 text-primary" />
                        Core Features
                      </h3>
                      <ul className="space-y-3">
                        {service.features.map((feature, idx) => (
                          <li key={idx} className="flex items-start gap-3">
                            <Check className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                            <span className="text-muted-foreground">{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-2xl font-semibold mb-4 flex items-center gap-2">
                        <Sparkles className="w-6 h-6 text-accent" />
                        AI Capabilities
                      </h3>
                      <ul className="space-y-3">
                        {service.aiCapabilities.map((capability, idx) => (
                          <li key={idx} className="flex items-start gap-3">
                            <Sparkles className="w-5 h-5 text-accent mt-0.5 flex-shrink-0" />
                            <span className="text-muted-foreground">{capability}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  <Button size="lg" className="mt-8" asChild>
                    <Link to={ROUTE_PATHS.CONTACT}>
                      Learn More
                      <ArrowRight className="ml-2 w-5 h-5" />
                    </Link>
                  </Button>
                </div>

                <div className={`${index % 2 === 1 ? 'md:order-1' : ''}`}>
                  <Card className="overflow-hidden border-2">
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-[500px] object-cover"
                    />
                  </Card>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      <section className="py-24">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springPresets.gentle}
            className="text-center mb-16"
          >
            <Badge className="mb-4 text-base px-6 py-2">Service Comparison</Badge>
            <h2 className="text-4xl md:text-5xl font-bold mb-6 tracking-tight">
              Choose Your Path to Success
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              All services include AI-powered analysis, dedicated human support, and transparent pricing.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ ...springPresets.gentle, delay: 0.2 }}
          >
            <Card className="overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="text-left p-6 font-semibold">Feature</th>
                      <th className="text-center p-6 font-semibold">Credit Repair</th>
                      <th className="text-center p-6 font-semibold">Tax Services</th>
                      <th className="text-center p-6 font-semibold">Business Consulting</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b">
                      <td className="p-6 font-medium">AI-Powered Analysis</td>
                      <td className="text-center p-6"><Check className="w-6 h-6 text-primary mx-auto" /></td>
                      <td className="text-center p-6"><Check className="w-6 h-6 text-primary mx-auto" /></td>
                      <td className="text-center p-6"><Check className="w-6 h-6 text-primary mx-auto" /></td>
                    </tr>
                    <tr className="border-b">
                      <td className="p-6 font-medium">Dedicated Human Advisor</td>
                      <td className="text-center p-6"><Check className="w-6 h-6 text-primary mx-auto" /></td>
                      <td className="text-center p-6"><Check className="w-6 h-6 text-primary mx-auto" /></td>
                      <td className="text-center p-6"><Check className="w-6 h-6 text-primary mx-auto" /></td>
                    </tr>
                    <tr className="border-b">
                      <td className="p-6 font-medium">24/7 Monitoring</td>
                      <td className="text-center p-6"><Check className="w-6 h-6 text-primary mx-auto" /></td>
                      <td className="text-center p-6 text-muted-foreground">Seasonal</td>
                      <td className="text-center p-6"><Check className="w-6 h-6 text-primary mx-auto" /></td>
                    </tr>
                    <tr className="border-b">
                      <td className="p-6 font-medium">Typical Timeline</td>
                      <td className="text-center p-6 text-muted-foreground">6-12 months</td>
                      <td className="text-center p-6 text-muted-foreground">Year-round</td>
                      <td className="text-center p-6 text-muted-foreground">3-18 months</td>
                    </tr>
                    <tr className="border-b">
                      <td className="p-6 font-medium">Starting Price</td>
                      <td className="text-center p-6 text-muted-foreground">$99/month</td>
                      <td className="text-center p-6 text-muted-foreground">$200+</td>
                      <td className="text-center p-6 text-muted-foreground">Custom</td>
                    </tr>
                    <tr>
                      <td className="p-6 font-medium">Free Consultation</td>
                      <td className="text-center p-6"><Check className="w-6 h-6 text-primary mx-auto" /></td>
                      <td className="text-center p-6"><Check className="w-6 h-6 text-primary mx-auto" /></td>
                      <td className="text-center p-6"><Check className="w-6 h-6 text-primary mx-auto" /></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </Card>
          </motion.div>
        </div>
      </section>

      <section className="py-24 bg-muted/30">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springPresets.gentle}
            className="text-center mb-16"
          >
            <Badge className="mb-4 text-base px-6 py-2">Our Process</Badge>
            <h2 className="text-4xl md:text-5xl font-bold mb-6 tracking-tight">
              How We Work With You
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              A proven methodology combining AI efficiency with personalized human guidance.
            </p>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid md:grid-cols-4 gap-8"
          >
            {[
              {
                step: '01',
                title: 'Free Consultation',
                description: 'We analyze your situation and create a customized action plan tailored to your goals.',
                image: IMAGES.TAX_SERVICES_2,
              },
              {
                step: '02',
                title: 'AI Analysis',
                description: 'Our AI system processes your data, identifying opportunities and creating optimization strategies.',
                image: IMAGES.AI_TECH_7,
              },
              {
                step: '03',
                title: 'Expert Review',
                description: 'Your dedicated advisor reviews AI findings and develops a personalized implementation plan.',
                image: IMAGES.CONSULTING_4,
              },
              {
                step: '04',
                title: 'Ongoing Support',
                description: 'Continuous monitoring, adjustments, and support until you achieve your financial goals.',
                image: IMAGES.AI_TECH_7,
              },
            ].map((item, index) => (
              <motion.div key={index} variants={staggerItem}>
                <Card className="h-full">
                  <CardHeader>
                    <div className="text-6xl font-bold text-primary/20 mb-4">{item.step}</div>
                    <CardTitle className="text-2xl">{item.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-base">{item.description}</CardDescription>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      <section className="py-24">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springPresets.gentle}
            className="max-w-4xl mx-auto text-center"
          >
            <Badge className="mb-6 text-base px-6 py-2">
              <Sparkles className="w-4 h-4 mr-2" />
              Ready to Get Started?
            </Badge>
            <h2 className="text-4xl md:text-6xl font-bold mb-6 tracking-tight">
              Your Financial Transformation Begins Today
            </h2>
            <p className="text-xl text-muted-foreground mb-8">
              Join thousands of satisfied clients who have improved their credit, maximized their tax savings, and grown their businesses with our AI-powered services.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" asChild className="text-lg px-8">
                <Link to={ROUTE_PATHS.CONTACT}>
                  Schedule Free Consultation
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Link>
              </Button>
              <Button size="lg" variant="outline" asChild className="text-lg px-8">
                <Link to={ROUTE_PATHS.ABOUT}>Learn About Our Team</Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
